import { MapPin, Phone, Mail, Clock, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n";
import { REVIEW_URL, REVIEW_LABEL, FOOTER_LINKS } from "@/utils";

export const Footer = () => {
  const { t, lang } = useLanguage();
  return (
    <footer className="relative bg-[#FAF9F6] border-t border-black/10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-icon.png" alt="ClearRoute Auto Glass" className="h-12 w-auto" />
            <span className="font-display font-bold text-xl tracking-tight">
              <span className="text-[#D4AF37]">Clear</span>Route
              <span className="block text-[10px] font-medium tracking-[0.3em] text-black/50 uppercase">Auto Glass</span>
            </span>
          </div>
          <p className="mt-5 text-black/50 text-sm italic">{t.footer.tagline}</p>
          <p className="mt-3 text-black/40 text-xs tracking-wide">{t.footer.langNote}</p>
          <a
            href={REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-google-review"
            className="mt-6 inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-xs font-bold px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            <Star className="w-3.5 h-3.5" fill="currentColor" />
            {REVIEW_LABEL[lang] || REVIEW_LABEL.en}
          </a>
        </div>

        <div>
          <h4 className="text-xs font-bold tracking-[0.25em] uppercase text-[#D4AF37] mb-6">{t.footer.contact}</h4>
          <ul className="space-y-4 text-sm text-black/65">
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
              <span>98 Foster St<br />Peabody, MA 01960</span>
            </li>
            <li>
              <a href="tel:+18576152967" className="flex items-center gap-3 hover:text-[#D4AF37] transition-colors" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-[#D4AF37] shrink-0" /> +1 857-615-2967
              </a>
            </li>
            <li>
              <a href="mailto:info@clearrouteglass.com" className="flex items-center gap-3 hover:text-[#D4AF37] transition-colors break-all" data-testid="footer-email">
                <Mail className="w-4 h-4 text-[#D4AF37] shrink-0" /> info@clearrouteglass.com
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-bold tracking-[0.25em] uppercase text-[#D4AF37] mb-6">{t.footer.hours}</h4>
          <ul className="space-y-4 text-sm text-black/65">
            <li className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#D4AF37] shrink-0" />{t.footer.weekday}: 8am – 7pm</li>
            <li className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#D4AF37] shrink-0" />{t.footer.sat}: 10am – 4pm</li>
            <li className="flex items-center gap-3 text-black/40"><Clock className="w-4 h-4 shrink-0" />{t.footer.sun}</li>
          </ul>
          <div className="mt-8 flex flex-col gap-2">
            <Link to="/blog" className="text-xs text-black/50 hover:text-[#B8860B] transition-colors tracking-widest uppercase font-semibold" data-testid="footer-blog-link">Blog</Link>
            <Link to="/track" className="text-xs text-black/50 hover:text-[#B8860B] transition-colors tracking-widest uppercase font-semibold" data-testid="footer-track-link">{(FOOTER_LINKS[lang] || FOOTER_LINKS.en).track}</Link>
          </div>
          <Link to="/admin" className="mt-6 inline-block text-xs text-black/30 hover:text-[#D4AF37] transition-colors tracking-widest uppercase" data-testid="footer-admin-link">
            {t.footer.admin}
          </Link>
        </div>
      </div>
      <div className="border-t border-black/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row justify-between gap-2 text-xs text-black/35">
          <span>© 2026 ClearRoute Auto Glass · {t.footer.rights}</span>
          <a href="https://clearrouteglass.com" className="hover:text-[#D4AF37] transition-colors">clearrouteglass.com</a>
        </div>
      </div>
    </footer>
  );
};
